// src/pages/NewProjectPage.js
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import "./NewProjectPage.css";

/* ---------- TABELA ZLECEŃ ---------- */
function OrdersSelectionTable({
  orders,
  selectedIds,
  onSelect,
  sortField,
  sortOrder,
  onSort,
}) {
  /* sortowanie ASC / DESC */
  const sorted = useMemo(() => {
    const compare = (a, b) => {
      let r = 0;
      if (sortField === "nr_ref") {
        const [aNum] = a.nr_ref.split("/"),
          [bNum] = b.nr_ref.split("/");
        r = +aNum - +bNum;
      } else if (sortField === "tytul") {
        r = a.tytul.localeCompare(b.tytul);
      } else if (sortField === "client_name") {
        r = (a.client_name || "").localeCompare(b.client_name || "");
      }
      return sortOrder === "asc" ? r : -r;
    };
    return [...orders].sort(compare);
  }, [orders, sortField, sortOrder]);

  const arrow = (field) =>
    sortField === field ? (sortOrder === "asc" ? " ▲" : " ▼") : "";

  return (
    <div className="orders-table-container">
      <table className="orders-table">
        <thead>
          <tr>
            <th>Wybierz</th>
            <th onClick={() => onSort("nr_ref")}>Nr.ref{arrow("nr_ref")}</th>
            <th onClick={() => onSort("tytul")}>Tytuł{arrow("tytul")}</th>
            <th onClick={() => onSort("client_name")}>
              Klient{arrow("client_name")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.length ? (
            sorted.map((o) => (
              <tr key={o.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(o.id)}
                    onChange={(e) => onSelect(e, o.id)}
                  />
                </td>
                <td>{o.nr_ref}</td>
                <td className="title-column">{o.tytul}</td>
                <td>{o.client_name || "—"}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className="no-data">
                Brak zleceń do wyświetlenia
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function NewProjectPage() {
  const nav = useNavigate();

  /* ---------- STANY ---------- */
  const [form, setForm] = useState({
    name: "",
    description: "",
    klient_id: "",
    deadline: "",
  });
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* ---------- FETCH ---------- */
  useEffect(() => {
    (async () => {
      try {
        const [cRes, oRes] = await Promise.all([
          fetch(`${API_URL}/clients`, { credentials: "include" }),
          fetch(`${API_URL}/orders`, { credentials: "include" }),
        ]);
        if (!cRes.ok || !oRes.ok) throw new Error();
        setClients(await cRes.json());
        setOrders(await oRes.json());
      } catch {
        setError("Nie udało się pobrać danych.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ---------- HANDLERY ---------- */
  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSelect = (e, id) =>
    setSelectedIds((prev) =>
      e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)
    );

  /* jednoklikowe przełączanie ASC / DESC */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Nazwa projektu jest wymagana.");

    try {
      const r = await fetch(`${API_URL}/projects`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, order_ids: selectedIds }),
      });
      if (!r.ok) throw new Error((await r.json()).error || "Błąd zapisu");
      nav("/projekty");
    } catch (err) {
      setError(err.message);
    }
  };

  /* ---------- FILTR ---------- */
  const visibleOrders = orders.filter((o) =>
    o.tytul.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <p>Ładowanie…</p>;

  /* ---------- RENDER ---------- */
  return (
    <div className="new-project-flex">
      {/* -------- LEWA 3/4 -------- */}
      <form onSubmit={handleSubmit} className="np-left">
        <h2>Nowy Projekt</h2>
        {error && <p className="error-message">{error}</p>}

        <div className="form-section">
          <div className="form-group">
            <label>Nazwa Projektu*:</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Opis Projektu:</label>
            <textarea
              name="description"
              rows="3"
              value={form.description}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Klient:</label>
            <select
              name="klient_id"
              value={form.klient_id}
              onChange={handleChange}
            >
              <option value="">— wybierz klienta —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Termin Wykonania*:</label>
            <input
              type="date"
              name="deadline"
              value={form.deadline}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="orders-selection-section">
          <h3>Zlecenia do przypisania</h3>
          <input
            className="search-input"
            placeholder="Szukaj po tytule…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <OrdersSelectionTable
            orders={visibleOrders}
            selectedIds={selectedIds}
            onSelect={handleSelect}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </div>
      </form>

      {/* -------- PRAWA 1/4 -------- */}
      <aside className="np-right">
        {/* ❶ Zamieniona kolejność przycisków  */}
        <button className="btn-submit np-btn" onClick={handleSubmit}>
          Utwórz
        </button>
        <button
          className="btn-secondary np-btn"
          onClick={() => nav("/projekty")}
        >
          Anuluj
        </button>
      </aside>
    </div>
  );
}
